"use client";
import React, { useCallback, useReducer } from "react";
import { useStorageContext } from "../context";
import { ReducerActionType, removeAction, setAction } from "../reducer";
/**
 * Utility function to get a new storage handler.
 *
 * Takes a data type and browser storage operations and returns a GenericStorage
 * instace for that data, using the provided accessor functions.
 */
export const makeGenericStorage = (name, { getItem, setItem, removeItem }, contextFactory) => {
    /**
     * Utility function to return a new version of the storage state.
     *
     * This is a shallow object that has access to storage. We use new versions
     * of it to let React know that there was a state change that should trigger
     * re-evaluation of components reading from storage.
     *
     * @private
     */
    const Context = contextFactory(name);
    /**
     * Generic browser store based on the data type.
     *
     * To be used for interacting with the browser's storage.
     *
     * @public
     */
    const useStorage = () => useStorageContext(Context);
    /**
     * Generic state initializer, using the passed in getItem function.
     *
     * @private
     */
    const makeStorageState = () => ({
        getItem,
    });
    /**
     * React reducer implementation for localStorage.
     *
     * Simple reducer that supports SET and REMOVE actions and refreshes the state
     * on change.
     *
     * @private
     */
    const storageReducer = (state, action) => {
        switch (action.type) {
            case ReducerActionType.SET:
                setItem(action.key, action.value, action.options);
                return makeStorageState();
            case ReducerActionType.REMOVE:
                removeItem(action.key, action.options);
                return makeStorageState();
            default: {
                const _ = action;
                throw new Error(`Invalid action: ${_}`);
            }
        }
    };
    /**
     *  Provider component for a browser storage.
     *
     * @public
     */
    const Provider = ({ children }) => {
        const [state, dispatch] = useReducer(storageReducer, makeStorageState());
        const setItem = useCallback((key, value, options) => {
            dispatch(setAction(key, value, options));
        }, []);
        const removeItem = useCallback((key, options) => {
            dispatch(removeAction(key, options));
        }, []);
        const store = {
            getItem: state.getItem,
            setItem,
            removeItem,
        };
        return React.createElement(Context.Provider, { value: store }, children);
    };
    return { useStorage, Provider };
};
//# sourceMappingURL=genericstorage.js.map